"use client";

import type { TaskListListOutput } from "@orsane/schema/task-lists";
import { matchSorter } from "match-sorter";
import * as React from "react";
import { TASK_ROW_HEIGHT } from "@/common/constants";
import { useTaskLists } from "@/queries/list-queries";
import TaskListBadge, { TaskListBadgeColorShape } from "../TaskListBadge";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

interface ListSelectProps {
    value: TaskListListOutput[];
    onChange: (values: TaskListListOutput[]) => void;
    trigger: (content: React.ReactNode, value: TaskListListOutput[]) => React.ReactElement;
}

export function ListSelect({ value, onChange, trigger }: ListSelectProps) {
    const [open, setOpen] = React.useState(false);
    const [search, setSearch] = React.useState("");
    const { data } = useTaskLists({ enabled: open });

    const handleUnselect = (v: TaskListListOutput) => {
        const newValues = value.filter((item) => item.id !== v.id);
        onChange(newValues);
    };

    const handleSelect = (v: string) => {
        const selectedList = data?.find((u) => u.id === Number(v)) as TaskListListOutput;
        if (!selectedList) {
            throw new Error("Selected list not found in list of lists");
        }

        const alreadyPresent = value.some((item) => item.id === selectedList.id);
        if (alreadyPresent) {
            // if already present, remove
            const newValues = value.filter((item) => item.id !== selectedList.id);
            onChange(newValues);
        } else {
            // if not present, add
            const newValues = [...value, selectedList];
            onChange(newValues);
        }
    };

    const unselectedLists = data?.filter((list) => !value.some((v) => v.id === list.id)) ?? [];
    const filteredLists = matchSorter(unselectedLists, search, { keys: ["name"] });

    return (
        <Popover
            open={open}
            onOpenChange={(o) => {
                setOpen(o);
                if (!o) setSearch("");
            }}
        >
            <PopoverTrigger
                render={trigger(
                    value.length > 0
                        ? value.map((value) => {
                              return <TaskListBadge taskList={value} key={value.id}></TaskListBadge>;
                          })
                        : null,
                    value
                )}
            />
            <PopoverContent sideOffset={-TASK_ROW_HEIGHT} align="start" className="w-full p-0 max-w-80 gap-0!">
                <div className="p-2 pb-0 flex flex-wrap overflow-hidden gap-1 min-h-[30px]">
                    {!value?.length && (
                        <div className="leading-none grid place-items-center pl-1 text-zinc-400 py-1">
                            No lists selected
                        </div>
                    )}
                    {value.map((tl) => {
                        return (
                            <TaskListBadge
                                onDelete={() => {
                                    handleUnselect(tl);
                                }}
                                showDelete={true}
                                taskList={tl}
                                key={tl.id}
                            />
                        );
                    })}
                </div>
                <Command shouldFilter={false}>
                    <CommandInput
                        autoFocus
                        placeholder="Search for a list..."
                        value={search}
                        onValueChange={setSearch}
                    />
                    <CommandList>
                        <CommandGroup>
                            {filteredLists.map((list) => {
                                return (
                                    <CommandItem
                                        value={list.id.toString()}
                                        key={list.id}
                                        keywords={[list.name]}
                                        onClick={(e) => e.stopPropagation()}
                                        onSelect={handleSelect}
                                    >
                                        <TaskListBadgeColorShape color={list.color} />
                                        {list.name}
                                    </CommandItem>
                                );
                            })}
                        </CommandGroup>
                    </CommandList>
                    <CommandEmpty>No lists found.</CommandEmpty>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
